import { ISanityBlockContent, ISanityTypographyBlock } from '@rbi-ctg/menu';
import { ICartEntryAdapter } from 'state/loyalty/hooks/types';

// Matches the SanityBlockRenderer `content` prop so consumers can pass block
// messaging straight through without casting.
export type PromoMessageBlock = ISanityBlockContent[] | ISanityTypographyBlock[] | null;

export interface PromoMessageContent {
  name?: string | null;
  loyaltyEngineId?: string | null;
  promoMessage?: string | null;
  promoMessageBlock?: PromoMessageBlock;
  activatedPromoMessageBlock?: PromoMessageBlock;
}

// The authoritative promotion snapshot returned by a re-read. Mirrors the
// activatable fields of BestPromotionResult so callers can decide whether a
// failed activation's promotion is still activatable without reaching for Apollo.
export interface PromotionRereadSnapshot {
  promotionId?: string;
  mustActivate?: boolean | null;
  userActivatedAt?: string | null;
}

export interface BestPromotionResult {
  promotionId?: string;
  mustActivate?: boolean | null;
  userActivatedAt?: string | null;
  content?: PromoMessageContent | null;
  // Refetches every active observer of the underlying query (parity with the
  // legacy refetchQueries({ include: [...] }) behavior), hiding Apollo/document
  // from consumers, and resolves this observer's authoritative fresh snapshot so
  // callers can react to the re-read without a separate query.
  refetchPromotion: () => Promise<PromotionRereadSnapshot>;
}

export interface BestPromotionSourceArgs {
  // Whether THIS source is the active one. Both sources stay mounted for stable
  // hook order; the inactive one self-skips.
  enabled: boolean;
  loyaltyId?: string;
  loyaltyEnabled: boolean;
  loyaltyLoading: boolean;
  cartEntries?: ICartEntryAdapter[];
  storeId?: string | null;
  skipStoreDayPartHours?: boolean;
  onActivateResolved?: () => void;
}

export interface ActivatePromotionArgs {
  loyaltyId: string;
  promotionId: string;
}

export interface ActivatePromotionResult {
  // Activates a must-activate promotion. Resolves once the mutation completes so
  // callers can refetch and update UI; hides Apollo/document from consumers.
  activatePromotion: (args: ActivatePromotionArgs) => Promise<void>;
}

// A promotion no longer needs an explicit activation prompt once it either does
// not require activation or has already been activated by the user.
export const shouldClearActivateParam = (
  promotion?: { mustActivate?: boolean | null; userActivatedAt?: string | null } | null
): boolean => Boolean(promotion && (!promotion.mustActivate || promotion.userActivatedAt));

// After a FAILED activation attempt, clear the auto-activate deep-link flag only
// once the failed promotion is authoritatively gone or no longer activatable, so
// a later must-activate promo cannot silently auto-activate. Keep the flag when
// the same promotion is still activatable so a transient failure can retry.
export const shouldClearAfterFailedReread = (
  failedPromotionId: string | undefined,
  reread: PromotionRereadSnapshot
): boolean => {
  if (!reread.promotionId) {
    return true;
  }
  // A different promotion replacing the failed one also counts as authoritatively
  // gone, so the flag should clear rather than auto-activate the replacement.
  if (reread.promotionId !== failedPromotionId) {
    return true;
  }
  return shouldClearActivateParam(reread);
};
